import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createChart, CrosshairMode } from 'lightweight-charts';
import axios from 'axios';
import { RefreshCw, TrendingUp, TrendingDown, Activity, Zap } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const SYMBOLS = [
  { symbol: 'BTCUSDT', name: 'Bitcoin', icon: '₿' },
  { symbol: 'ETHUSDT', name: 'Ethereum', icon: 'Ξ' },
  { symbol: 'SOLUSDT', name: 'Solana', icon: '◎' },
  { symbol: 'BNBUSDT', name: 'BNB', icon: '◆' }
];

const TIMEFRAMES = ['1m', '5m', '15m', '1h', '4h', '1d'];

// Custom time formatter for lightweight-charts to avoid locale issues
const timeFormatter = (time) => {
  const date = new Date(time * 1000);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

export default function Dashboard() {
  const [symbol, setSymbol] = useState('BTCUSDT');
  const [timeframe, setTimeframe] = useState('15m');
  const [marketMode, setMarketMode] = useState('SPOT'); // SPOT or FUTURES
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSimulated, setIsSimulated] = useState(false);
  const [leverage, setLeverage] = useState(5);
  
  const chartContainerRef = useRef(null);
  const stochContainerRef = useRef(null);
  const chartRef = useRef(null);
  const stochChartRef = useRef(null);

  // Fetch leverage when in futures mode
  useEffect(() => {
    if (marketMode === 'FUTURES') {
      axios.get(`${API_URL}/api/futures/leverage/${symbol}`)
        .then(res => setLeverage(res.data.leverage))
        .catch(() => setLeverage(5));
    }
  }, [marketMode, symbol]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/market/klines-with-indicators/${symbol}`,
        { params: { interval: timeframe, days: 30, mode: marketMode } }
      );
      setData(response.data);
      setIsSimulated(response.data.simulated);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error fetching market data');
    } finally {
      setLoading(false);
    }
  }, [symbol, timeframe, marketMode]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [fetchData]);

  // Main chart
  useEffect(() => {
    if (!chartContainerRef.current || !data) return;

    // Clean up previous chart
    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
    }

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { color: '#0B0D0F' },
        textColor: '#9AA3AF'
      },
      grid: {
        vertLines: { color: '#12161C' },
        horzLines: { color: '#12161C' }
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: '#1E2329' },
      timeScale: { 
        borderColor: '#1E2329',
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: timeFormatter
      },
      localization: {
        timeFormatter: timeFormatter
      },
      width: chartContainerRef.current.clientWidth,
      height: 400
    });

    chartRef.current = chart;

    // Candlestick series (v4 API)
    const candleSeries = chart.addCandlestickSeries({
      upColor: '#16A34A',
      downColor: '#DC2626',
      borderUpColor: '#16A34A',
      borderDownColor: '#DC2626',
      wickUpColor: '#9AA3AF',
      wickDownColor: '#9AA3AF'
    });
    candleSeries.setData(data.candles);

    // Volume series (v4 API)
    const volumeSeries = chart.addHistogramSeries({
      priceFormat: { type: 'volume' },
      priceScaleId: 'volume'
    });
    chart.priceScale('volume').applyOptions({
      scaleMargins: { top: 0.85, bottom: 0 }
    });
    volumeSeries.setData(data.volume);

    // EMA lines (v4 API)
    if (data.ema20?.length > 0) {
      const ema20Series = chart.addLineSeries({
        color: '#22D3EE',
        lineWidth: 1,
        title: 'EMA 20'
      });
      ema20Series.setData(data.ema20);
    }

    if (data.ema50?.length > 0) {
      const ema50Series = chart.addLineSeries({
        color: '#F59E0B',
        lineWidth: 1,
        title: 'EMA 50'
      });
      ema50Series.setData(data.ema50);
    }

    if (data.ema200?.length > 0) {
      const ema200Series = chart.addLineSeries({
        color: '#94A3B8',
        lineWidth: 1,
        title: 'EMA 200'
      });
      ema200Series.setData(data.ema200);
    }

    chart.timeScale().fitContent();

    // Resize observer
    const resizeObserver = new ResizeObserver(entries => {
      if (entries[0]) {
        chart.applyOptions({ width: entries[0].contentRect.width });
      }
    });
    resizeObserver.observe(chartContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [data]);

  // Stochastic chart
  useEffect(() => {
    if (!stochContainerRef.current || !data) return;

    // Clean up previous chart
    if (stochChartRef.current) {
      stochChartRef.current.remove();
      stochChartRef.current = null;
    }

    const chart = createChart(stochContainerRef.current, {
      layout: {
        background: { color: '#0B0D0F' },
        textColor: '#9AA3AF'
      },
      grid: {
        vertLines: { color: '#12161C' },
        horzLines: { color: '#12161C' }
      },
      rightPriceScale: { 
        borderColor: '#1E2329',
        scaleMargins: { top: 0.1, bottom: 0.1 }
      },
      timeScale: { 
        borderColor: '#1E2329',
        timeVisible: true,
        visible: false,
        tickMarkFormatter: timeFormatter
      },
      localization: {
        timeFormatter: timeFormatter
      },
      width: stochContainerRef.current.clientWidth,
      height: 140
    });

    stochChartRef.current = chart;

    // Stochastic K line (v4 API)
    if (data.stoch_k?.length > 0) {
      const kSeries = chart.addLineSeries({
        color: '#38BDF8',
        lineWidth: 1.5,
        title: '%K'
      });
      kSeries.setData(data.stoch_k);
    }

    // Stochastic D line (v4 API)
    if (data.stoch_d?.length > 0) {
      const dSeries = chart.addLineSeries({
        color: '#FDE68A',
        lineWidth: 1.5,
        title: '%D'
      });
      dSeries.setData(data.stoch_d);
    }

    // Overbought/Oversold lines (v4 API)
    const overBoughtLine = chart.addLineSeries({
      color: 'rgba(239, 68, 68, 0.3)',
      lineWidth: 1,
      lineStyle: 2
    });
    const overSoldLine = chart.addLineSeries({
      color: 'rgba(34, 197, 94, 0.3)',
      lineWidth: 1,
      lineStyle: 2
    });

    if (data.stoch_k?.length > 0) {
      overBoughtLine.setData(data.stoch_k.map(d => ({ time: d.time, value: 80 })));
      overSoldLine.setData(data.stoch_k.map(d => ({ time: d.time, value: 20 })));
    }

    chart.timeScale().fitContent();

    // Resize observer
    const resizeObserver = new ResizeObserver(entries => {
      if (entries[0]) {
        chart.applyOptions({ width: entries[0].contentRect.width });
      }
    });
    resizeObserver.observe(stochContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [data]);

  const stats = data?.stats || {};
  const currentSymbol = SYMBOLS.find(s => s.symbol === symbol);

  const handleLeverageChange = async (newLeverage) => {
    try {
      await axios.post(`${API_URL}/api/futures/leverage`, {
        symbol,
        leverage: newLeverage
      });
      setLeverage(newLeverage);
      toast.success(`Leverage set to ${newLeverage}x`);
    } catch (error) {
      toast.error('Error setting leverage');
    }
  };

  return (
    <div>
      {/* Header */}
      <header className="page-header">
        <div className="page-header-content">
          <div className="header-left">
            {/* Market Mode Toggle */}
            <div style={{ display: 'flex', backgroundColor: '#0F1318', borderRadius: '8px', padding: '4px' }}>
              <button
                className={`timeframe-tab ${marketMode === 'SPOT' ? 'active' : ''}`}
                onClick={() => setMarketMode('SPOT')}
                data-testid="mode-spot"
                style={{ minWidth: '70px' }}
              >
                Spot
              </button>
              <button
                className={`timeframe-tab ${marketMode === 'FUTURES' ? 'active' : ''}`}
                onClick={() => setMarketMode('FUTURES')}
                data-testid="mode-futures"
                style={{ 
                  minWidth: '70px',
                  backgroundColor: marketMode === 'FUTURES' ? '#F59E0B' : undefined 
                }}
              >
                <Zap size={14} style={{ marginRight: '4px' }} />
                Futures
              </button>
            </div>

            {/* Symbol Select */}
            <select
              value={symbol}
              onChange={(e) => setSymbol(e.target.value)}
              className="form-select"
              style={{ width: '180px' }}
              data-testid="symbol-select"
            >
              {SYMBOLS.map(s => (
                <option key={s.symbol} value={s.symbol}>
                  {s.icon} {s.name} ({s.symbol})
                </option>
              ))}
            </select>

            {/* Leverage Selector (Futures only) */}
            {marketMode === 'FUTURES' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.75rem', color: '#9AA3AF' }}>Leverage:</span>
                <select
                  value={leverage}
                  onChange={(e) => handleLeverageChange(parseInt(e.target.value))}
                  className="form-select"
                  style={{ width: '80px' }}
                  data-testid="leverage-select"
                >
                  {[1, 2, 3, 5, 10].map(l => (
                    <option key={l} value={l}>{l}x</option>
                  ))}
                </select>
              </div>
            )}

            {/* Timeframe Tabs */}
            <div className="timeframe-tabs">
              {TIMEFRAMES.map(tf => (
                <button
                  key={tf}
                  className={`timeframe-tab ${timeframe === tf ? 'active' : ''}`}
                  onClick={() => setTimeframe(tf)}
                  data-testid={`timeframe-tab-${tf}`}
                >
                  {tf}
                </button>
              ))}
            </div>
          </div>

          <div className="header-right">
            {marketMode === 'FUTURES' && (
              <span className="badge" style={{ backgroundColor: 'rgba(245, 158, 11, 0.15)', color: '#F59E0B' }}>
                <Zap size={12} style={{ marginRight: '4px' }} />
                USDT-M Perpetual
              </span>
            )}
            {isSimulated && (
              <span className="badge" style={{ backgroundColor: 'rgba(148, 163, 184, 0.15)', color: '#94A3B8' }}>
                Simulated Data
              </span>
            )}
            <button 
              className="btn btn-secondary btn-sm"
              onClick={fetchData}
              disabled={loading}
              data-testid="refresh-button"
            >
              <RefreshCw size={16} className={loading ? 'loading-spinner' : ''} />
              Refresh
            </button>
          </div>
        </div>
      </header>

      {/* Page Content */}
      <div className="page-container">
        {/* Stats Grid */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Price</div>
            <div className="stat-value font-mono" data-testid="current-price">
              ${stats.price?.toLocaleString() || '---'}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">24h Change</div>
            <div className={`stat-value font-mono ${stats.change_24h >= 0 ? 'positive' : 'negative'}`}>
              {stats.change_24h >= 0 ? '+' : ''}{stats.change_24h?.toFixed(2) || '0.00'}%
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">24h High</div>
            <div className="stat-value font-mono positive">
              ${stats.high_24h?.toLocaleString() || '---'}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">24h Low</div>
            <div className="stat-value font-mono negative">
              ${stats.low_24h?.toLocaleString() || '---'}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Stochastic</div>
            <div className={`stat-value font-mono ${stats.stoch_k > 80 ? 'negative' : stats.stoch_k < 20 ? 'positive' : ''}`}>
              {stats.stoch_k?.toFixed(1) || '---'}
            </div>
            <div className="stat-change">
              {stats.stoch_k > 80 ? 'Overbought' : stats.stoch_k < 20 ? 'Oversold' : 'Neutral'}
            </div>
          </div>
        </div>

        {/* Main Chart */}
        <div className="chart-container" style={{ marginBottom: '1.5rem' }}>
          <div className="chart-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span style={{ fontWeight: 600, color: '#E6E8EC' }}>
                {currentSymbol?.name} / USDT
              </span>
              <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem' }}>
                <span style={{ color: '#22D3EE' }}>─ EMA 20</span>
                <span style={{ color: '#F59E0B' }}>─ EMA 50</span>
                <span style={{ color: '#94A3B8' }}>─ EMA 200</span>
              </div>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#9AA3AF' }}>
              EMA: {stats.ema_20?.toFixed(2)} / {stats.ema_50?.toFixed(2)} / {stats.ema_200?.toFixed(2)}
            </div>
          </div>
          <div 
            ref={chartContainerRef} 
            className="chart-wrapper"
            data-testid="candles-chart"
          />
        </div>

        {/* Stochastic Panel */}
        <div className="chart-container">
          <div className="chart-header">
            <span style={{ fontWeight: 600, color: '#E6E8EC' }}>Stochastic (14, 3, 3)</span>
            <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem' }}>
              <span style={{ color: '#38BDF8' }}>─ %K: {stats.stoch_k?.toFixed(1)}</span>
              <span style={{ color: '#FDE68A' }}>─ %D: {stats.stoch_d?.toFixed(1)}</span>
            </div>
          </div>
          <div 
            ref={stochContainerRef}
            style={{ height: '140px', padding: '0.5rem' }}
            data-testid="stochastic-panel"
          />
        </div>

        {/* Indicator Legend */}
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <div className="card-content" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <span style={{ fontSize: '0.75rem', color: '#9AA3AF' }}>Trend (EMA 50): </span>
              {stats.price > stats.ema_50 ? (
                <span style={{ color: '#22C55E', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                  <TrendingUp size={14} /> Bullish (Only Longs)
                </span>
              ) : (
                <span style={{ color: '#EF4444', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                  <TrendingDown size={14} /> Bearish (Only Shorts)
                </span>
              )}
            </div>
            <div>
              <span style={{ fontSize: '0.75rem', color: '#9AA3AF' }}>Trend (EMA 200): </span>
              {stats.price > stats.ema_200 ? (
                <span style={{ color: '#22C55E', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                  <TrendingUp size={14} /> Above
                </span>
              ) : (
                <span style={{ color: '#EF4444', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                  <TrendingDown size={14} /> Below
                </span>
              )}
            </div>
            <div>
              <span style={{ fontSize: '0.75rem', color: '#9AA3AF' }}>Stochastic: </span>
              <span style={{ 
                color: stats.stoch_k > 80 ? '#EF4444' : stats.stoch_k < 20 ? '#22C55E' : '#9AA3AF' 
              }}>
                <Activity size={14} style={{ display: 'inline', marginRight: '4px' }} />
                {stats.stoch_k > 80 ? 'Overbought Zone' : stats.stoch_k < 20 ? 'Oversold Zone' : 'Neutral Zone'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
